import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  DeliveryQueueFilter,
  DeliverySettings,
  DeliveryStatusInput,
  StaffDelivery,
} from './types';

const LIVE_MS = 15_000;

/**
 * Under `dispensary` so the "Dispensed" step (packing, or a counter hand-over
 * that cancels a requested delivery) refreshes the delivery queue too.
 */
const all = ['dispensary', 'deliveries'] as const;

/** GET /dispensary/deliveries?status (dispensary:read, branch-scoped). */
export function useDeliveries(status: DeliveryQueueFilter = 'open') {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: [...all, branch, status],
    queryFn: async ({ signal }) =>
      (
        await api.get<StaffDelivery[]>('/dispensary/deliveries', {
          signal,
          query: { status },
        })
      ).data,
    enabled: branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

/** GET /dispensary/delivery-settings (dispensary:read or services:manage). */
export function useDeliverySettings() {
  return useQuery({
    queryKey: ['dispensary', 'delivery-settings'],
    queryFn: async ({ signal }) =>
      (
        await api.get<DeliverySettings>('/dispensary/delivery-settings', {
          signal,
        })
      ).data,
    staleTime: 5 * 60_000,
  });
}

/**
 * RL-1655 (gap #96): PUT /dispensary/delivery-settings (services:manage).
 * Fields sent replace; times are minutes after midnight (branch time).
 */
export function useSaveDeliverySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      feeSen: number | null;
      branchIds: string[];
      cutoffMin: number;
      deliverByMin: number;
    }) =>
      (await api.put<DeliverySettings>('/dispensary/delivery-settings', input))
        .data,
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: ['dispensary', 'delivery-settings'],
      }),
  });
}

/**
 * POST /dispensary/deliveries/{id}/status (dispensary:dispense): a courier
 * step, versioned. The queue is refreshed after success and after a 409 (the
 * delivery changed or moved on).
 */
export function useDeliveryStatus() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: all });
  return useMutation({
    mutationFn: async (v: { id: string; body: DeliveryStatusInput }) =>
      (
        await api.post<StaffDelivery>(
          `/dispensary/deliveries/${v.id}/status`,
          v.body,
        )
      ).data,
    onSuccess: refresh,
    onError: refresh,
  });
}
